import { motion } from "framer-motion";

/**
 * Live pitch-deck preview.
 * Renders a mini simulation of the deck cover using the current wizard state.
 * Updates in realtime as the user types / picks a concept / uploads media.
 */
export default function LivePreview({ info, mainImage, gallery, concept, pages }) {
  const palette = concept?.palette || ["#0A0A0A", "#E50914", "#FFFFFF", "#7B7B7B"];
  const bg = palette[0];
  const accent = palette[1];
  const heading = concept?.typography?.heading || "Clash Display";
  const stageName = (info?.stage_name || "Your Name").slice(0, 22);
  const headline = info?.headline || concept?.tagline || "Your professional headline appears here.";
  const pageCount = pages?.length || 0;
  const conceptName = concept?.name || "Live preview";

  return (
    <div className="sticky top-24">
      <div className="kd-tag mb-3">Live preview</div>
      <motion.div
        layout
        className="rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
      >
        {/* Cover mockup */}
        <div
          className="relative aspect-[3/4] overflow-hidden"
          style={{ background: bg }}
        >
          {/* AI-ish red bloom */}
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(circle at 90% 10%, ${accent}55, transparent 55%)`,
            }}
          />
          {/* Character image right half */}
          {mainImage && (
            <img
              src={mainImage}
              alt="cover"
              className="absolute right-0 top-0 h-full w-1/2 object-cover"
              style={{ maskImage: "linear-gradient(90deg, transparent 0%, black 30%)" }}
            />
          )}
          {/* Content overlay */}
          <div className="absolute inset-0 p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div className="text-[9px] tracking-[0.25em] uppercase text-white/60">Electronic Press Kit</div>
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: accent, boxShadow: `0 0 8px ${accent}` }} />
            </div>
            <div>
              <div
                className="text-white kd-display leading-[0.9] tracking-tight"
                style={{ fontFamily: `"${heading}", "Clash Display", sans-serif`, fontSize: "clamp(28px, 3.4vw, 40px)" }}
              >
                {stageName}
              </div>
              <div className="mt-2 text-white/70 text-[11px] leading-relaxed max-w-[85%]">
                {headline.slice(0, 90)}
              </div>
              <div className="mt-3 h-[2px] w-8" style={{ background: accent }} />
              <div className="mt-2 text-[9px] text-white/45 tracking-[0.2em] uppercase">
                {conceptName}
              </div>
            </div>
          </div>
        </div>

        {/* Below-cover strip: page count + gallery thumbs + palette */}
        <div className="bg-black/70 backdrop-blur-lg p-3 border-t border-white/[0.06]">
          <div className="flex items-center gap-2">
            {palette.slice(0, 4).map((c) => (
              <div key={c} className="w-3.5 h-3.5 rounded-full border border-white/10" style={{ background: c }} />
            ))}
            <div className="ml-auto flex items-center gap-2 text-[10px] text-zinc-400">
              <span>{pageCount || "—"} pages</span>
              {gallery?.length ? <span>· {gallery.length} media</span> : null}
            </div>
          </div>
          {gallery && gallery.length > 0 && (
            <div className="mt-2 grid grid-cols-4 gap-1">
              {gallery.slice(0, 4).map((g, i) => (
                <img key={i} src={g} alt="" className="w-full aspect-square object-cover rounded-md" />
              ))}
            </div>
          )}
        </div>

        {/* Page-list ribbon */}
        {pages && pages.length > 0 && (
          <div className="bg-black/50 border-t border-white/[0.06] px-3 py-2 max-h-40 overflow-y-auto">
            {pages.slice(0, 8).map((p, i) => (
              <div key={p.id || i} className="flex items-center gap-2 py-1 text-[10px]">
                <span className="text-zinc-600 w-5">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-zinc-200 truncate">{p.title}</span>
                <span className="ml-auto text-zinc-600 uppercase tracking-[0.2em]">{p.module}</span>
              </div>
            ))}
            {pages.length > 8 && <div className="text-[10px] text-zinc-600 mt-1">+{pages.length - 8} more</div>}
          </div>
        )}
      </motion.div>
      <div className="mt-3 text-[10px] text-zinc-500 text-center">
        Preview updates as you type. Final deck is landscape, cinema-graded.
      </div>
    </div>
  );
}
